import { ParentRelationshipType } from '../api/api';
import { TaskCreationTask } from '../models/TaskCreationTask';
import { CreateRelationOptions } from '../models/TaskRelations';
import { getPreparationTaskDates } from './preparationTaskDates';

interface MainTaskDates {
  earliestStartDate?: string;
  deadline?: string;
}

const addDays = (date: string, days: number) => {
  const result = new Date(date);
  if (isNaN(result.getTime())) return undefined;

  result.setUTCDate(result.getUTCDate() + days);
  return result.toISOString();
};

/**
 * Turns the relation picked in the relations diagram into the fields the create work task flow
 * starts out with, so the technician does not have to repeat the choice.
 */
export const getRelationTaskSeed = (
  relation: CreateRelationOptions,
  mainTask: MainTaskDates
): Partial<TaskCreationTask> => {
  const { minDaysFromMainTask, maxDaysFromMainTask } = relation;

  switch (relation.parentRelationshipType) {
    case ParentRelationshipType.PreparationForParent:
      return {
        parentRelationshipType: ParentRelationshipType.PreparationForParent,
        minTimeBeforeMainTaskDays: minDaysFromMainTask,
        maxTimeBeforeMainTaskDays: maxDaysFromMainTask,
        // The task may start as early as the widest end of the window allows.
        ...getPreparationTaskDates(mainTask.earliestStartDate, mainTask.deadline, maxDaysFromMainTask)
      };

    // Both keep the new task on the parent's visit - the same work order and therefore the same
    // time window - so neither needs a day window of its own.
    case ParentRelationshipType.ExecutedWithParent:
    case ParentRelationshipType.FollowUpSameWorkOrderAsParent:
      return {
        parentRelationshipType: relation.parentRelationshipType,
        earliestStartDate: mainTask.earliestStartDate,
        deadline: mainTask.deadline
      };

    default: {
      const mainTaskEnd = mainTask.deadline ?? mainTask.earliestStartDate;
      return {
        parentRelationshipType: ParentRelationshipType.FollowUpToParent,
        minTimeBeforeMainTaskDays: minDaysFromMainTask,
        maxTimeBeforeMainTaskDays: maxDaysFromMainTask,
        // A follow-up cannot start before the window opens, and should be done before it closes.
        earliestStartDate:
          mainTaskEnd && minDaysFromMainTask !== undefined ? addDays(mainTaskEnd, minDaysFromMainTask) : undefined,
        deadline:
          mainTaskEnd && maxDaysFromMainTask !== undefined ? addDays(mainTaskEnd, maxDaysFromMainTask) : undefined
      };
    }
  }
};
